import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@material-ui/core/Container';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import CircleIcon from '@material-ui/icons/FiberManualRecord';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Ran } from '../Utils/utils';
import {
  controller_type_mapping,
  temp_controller_type_mapping,
  statusColors
} from '../Constants/constants';

import { useSnapshot } from 'valtio';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import AddIcon from '@material-ui/icons/Add';
import SettingsIcon from '@material-ui/icons/Settings';
import DevicesIcon from '@material-ui/icons/Devices';
import PublicIcon from '@mui/icons-material/Public';

import getStateManager from '../StateManager.js';

import { getControllerApiVersion, isZoned } from '../Utils/utils.js';

import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useTheme } from '@mui/material/styles';
import { ResponsiveActionButton } from '../components/ResponsiveActionButton';
import { useIsMobile } from '../Hooks/useMedia';

const mm = {
  marginTop: 10
};

export function AddSimulationButton({ onClick }) {
  const { t } = useTranslation();

  return (
    <Button onClick={onClick} startIcon={<AddIcon />}>
      {t('add_simulation')}
    </Button>
  );
}

function canRenameController(controller) {
  return getControllerApiVersion(controller) >= 'BAICB' && (
    Boolean(controller.simulation) || [5052, 5053, 5055, 5155].includes(Number(controller.type))
  );
}

function ControllerActionsMenu({ controller, canDelete, onDelete, onCopy, onRename }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuId = `controller-actions-menu-${encodeURIComponent(controller.id)}`;
  const closeMenu = () => setAnchorEl(null);
  const selectAction = (action) => {
    closeMenu();
    action(controller.id);
  };

  return (
    <>
      <ResponsiveActionButton
        compact
        icon={<MoreHorizIcon />}
        label={t('actions')}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? menuId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {canDelete && (
          <MenuItem onClick={() => selectAction(onDelete)}>{t('delete')}</MenuItem>
        )}
        <MenuItem onClick={() => selectAction(onCopy)}>{t('copy_controller_id')}</MenuItem>
        {canDelete && onRename && canRenameController(controller) && (
          <MenuItem onClick={() => selectAction(onRename)}>{t('rename')}</MenuItem>
        )}
      </Menu>
    </>
  );
}

export function MobileControllerTable({ controllers, canDelete, onDelete, onCopy, onRename }) {
  const { t } = useTranslation();

  return (
    <>
      <Table aria-label="table of controllers" style={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>{`Name / ${t('type')}`}</TableCell>
            <TableCell align="center" style={{ width: 64 }}>{t('actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {controllers.map((controller) => (
            <TableRow key={controller.id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon
                    data-testid={`controller-status-${controller.id}`}
                    style={{ color: statusColors[controller?.connection?.status ?? 'bad'] }}
                  />
                  <Link
                    style={{ color: 'inherit', minWidth: 0, overflowWrap: 'anywhere' }}
                    to={
                      `/controller/${controller.id}` +
                      (isZoned(controller) ? '/zones/zone_0/info' : '/info')
                    }
                  >
                    {controller.name}
                  </Link>
                </Box>
                <Box
                  data-testid={`controller-secondary-${controller.id}`}
                  sx={{
                    marginTop: 0.5
                  }}
                >
                  <span
                    data-testid={`controller-type-${controller.id}`}
                    style={{ paddingLeft: 32 }}
                  >
                    {controller_type_mapping[controller.type]}
                  </span>
                </Box>
              </TableCell>
              <TableCell align="center" padding="none">
                <ControllerActionsMenu
                  controller={controller}
                  canDelete={canDelete}
                  onDelete={onDelete}
                  onCopy={onCopy}
                  onRename={onRename}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export default function ManagePage({ user }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const mobile = useIsMobile(960);

  const [showWsWarning, setShowWsWarning] = useState(false);

  const stateManager = useSnapshot(getStateManager());
  const [cur_controller_id, setCurControllerId] = React.useState('');
  const controller = stateManager.getControllerById(cur_controller_id);

  const wsManager = stateManager?.wsManager;
  useEffect(() => {
    let closedTimeout;
    // The manager and native socket are not reactive. Read the current socket
    // each time so delayed initialization and reconnect replacements are covered.
    const updateWarning = () => {
      const readyState = wsManager?.socket?.readyState;
      if (readyState === WebSocket.CLOSED) {
        if (closedTimeout === undefined) {
          closedTimeout = window.setTimeout(() => setShowWsWarning(true), 2000);
        }
        return;
      }
      window.clearTimeout(closedTimeout);
      closedTimeout = undefined;
      setShowWsWarning(readyState !== WebSocket.OPEN);
    };
    updateWarning();
    const interval = window.setInterval(updateWarning, 250);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(closedTimeout);
    };
  }, [wsManager]);

  const [open_add_simulation, setOpenAddSimulation] = React.useState(false);
  const openAddDialog = () => {
    setOpenAddSimulation(true);
  };
  const closeAddDialog = () => {
    setOpenAddSimulation(false);
  };

  const [open_delete_dialog, setOpenDeleteDialog] = React.useState(false);
  const openDeleteDialog = (controller_id) => {
    setCurControllerId(controller_id);
    setOpenDeleteDialog(true);
  };
  const closeDeleteDialog = () => {
    setCurControllerId('');
    setOpenDeleteDialog(false);
  };

  const [open_rename_dialog, setOpenRenameDialog] = React.useState(false);
  const openRenameDialog = (controller_id) => {
    setCurControllerId(controller_id);
    setOpenRenameDialog(true);
  };
  const closeRenameDialog = () => setOpenRenameDialog(false);

  const [restrictions, setAccessRights] = React.useState({ general: { read: true, write: false } });
  useEffect(() => {
    if (user.access_rights) setAccessRights(user.access_rights);
  }, [user]);


  const copyControllerID = (controller_id) => {
  navigator.clipboard.writeText(controller_id);
  enqueueSnackbar('Controller ID "' + controller_id + '" copied to clipboard!', {
    variant: 'success'
  });
  };

  return (
    <>
    <Container style={mm}>
      <AddDialog
        open={open_add_simulation}
        handleClose={closeAddDialog}
        wsManager={stateManager.wsManager}
      />
      <DeleteDialog
        open={open_delete_dialog}
        controller={controller}
        wsManager={stateManager.wsManager}
        handleClose={closeDeleteDialog}
        command="delete_controller"
      />
      {open_rename_dialog && controller && (
        <RenameDialog
          controller={controller}
          wsManager={stateManager.wsManager}
          canWrite={restrictions.general.write}
          handleClose={closeRenameDialog}
        />
      )}
      <TableContainer component={Paper}>
        {mobile ? (
          <MobileControllerTable
            controllers={stateManager.controllers}
            canDelete={restrictions.general.write}
            onDelete={openDeleteDialog}
            onCopy={copyControllerID}
            onRename={openRenameDialog}
          />
        ) : (
          <Table aria-label="table of controllers" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>{t('type')}</TableCell>
              <TableCell align="center" style={{ width: 64 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stateManager.controllers.map((controller) => (
              <TableRow key={controller.id}>
                <TableCell style={{ width: '20%' }}>
                  <CircleIcon
                    style={{ color: statusColors[controller?.connection?.status ?? 'bad'] }}
                  />
                </TableCell>
                <TableCell
                  scope="row"
                  component={Link}
                  style={{ width: '30%' }}
                  to={
                    `/controller/${controller.id}` +
                    (isZoned(controller) ? '/zones/zone_0/info' : '/info')
                  }
                >
                  {controller.name}
                </TableCell>
                <TableCell sx={{ width: '25%' }}>{controller_type_mapping[controller.type]}</TableCell>
                <TableCell align="center" style={{ width: 64 }}>
                  <ControllerActionsMenu
                    controller={controller}
                    canDelete={restrictions.general.write}
                    onDelete={openDeleteDialog}
                    onCopy={copyControllerID}
                    onRename={openRenameDialog}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </TableContainer>
      {(stateManager.simulation === undefined || stateManager.simulation === null) &&
        restrictions.general.write && (
          <Box sx={{ marginTop: 2 }}>
            <AddSimulationButton onClick={openAddDialog} />
          </Box>
        )}
    </Container>
    {showWsWarning && (
        <div
          title="This delay might be caused by Firefox delaying WebSocket abort/cleanup."
          style={{
            position: "fixed",
            right: "16px",
            bottom: "16px",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 12px",
            borderRadius: "999px",
            background: "rgba(30,30,30,0.92)",
            color: "white",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            fontSize: "14px",
          }}
        >
          <span>WebSocket loading…</span>

          <button
            onClick={() => {window.location.reload();}}
            style={{
              border: "none",
              borderRadius: "999px",
              padding: "6px 10px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      )}
    </>
  );
}

export function RenameDialog({ controller, wsManager, canWrite, handleClose }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState(controller.name);
  const [submittedName, setSubmittedName] = useState(null);
  const invalidName = new Blob([name]).size > 63 || name.includes('\0');
  const disabled = !canWrite || !canRenameController(controller) || invalidName ||
    name.length === 0 || name === controller.name;

  useEffect(() => {
    if (submittedName === null || controller.name !== submittedName) return;
    setSubmittedName(null);
    enqueueSnackbar(t('display_name_saved'), { variant: 'success' });
    if (name === submittedName) handleClose();
  }, [controller.name, submittedName, name, enqueueSnackbar, t, handleClose]);

  const save = () => {
    if (disabled) return;
    try {
      if (wsManager.sendWSMsg(controller, 'update_display_name', { display_name: name })) {
        setSubmittedName(name);
        return;
      }
    } catch (_) {}
    enqueueSnackbar(t('display_name_unavailable'), { variant: 'error' });
  };

  return (
    <DialogWithEnterAction
      open
      onClose={handleClose}
      onEnterAction={save}
      enterActionDisabled={disabled}
      aria-labelledby="rename-controller-title"
    >
      <DialogTitle id="rename-controller-title">{t('rename')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="controller-display-name"
          label={t('name')}
          value={name}
          fullWidth
          error={invalidName}
          helperText={invalidName ? t('display_name_invalid') : ''}
          onChange={(event) => setName(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button onClick={save} color="primary" disabled={disabled}>{t('save')}</Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function AddDialog({ open, handleClose, wsManager }) {
  let { t } = useTranslation();
  const [values, setValues] = React.useState({ name: '', type: 5053 });
  const validateForm = (event) => {
    if (values.name) {
      //Add connection to websocket server
      const display_name = values.name;
      const internal_id = display_name.toLowerCase().replace(' ', '_');
      var json_request = {
        command: 'mock_initialize',
        cookie: document.cookie,
        internal_id: internal_id
      };
      json_request.data = {};
      json_request.data['display_name'] = display_name;
      json_request.data['type'] = parseInt(values.type);
      json_request.data['project_id'] = parseInt(values.type) === 5155 ? 0 : 0;
      const mac_address = Ran(0, 9) + '-' + Ran(0, 9) + '-' + Ran(0, 9) + '-' + Ran(9, 0);
      json_request.data['mac_address'] = mac_address;
      wsManager.socket.send(JSON.stringify(json_request));
      window.location.reload();
    } else {
      handleClose();
    }
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('add_simulation')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label="Name"
          type="text"
          fullWidth
          onChange={(event) => {
            const sanitizedValue = event.target.value.replace(/#/g, ''); // HotFix: Removes all '#' characters
            setValues({ ...values, name: sanitizedValue });
          }}
        />
        <Select
          fullWidth
          value={values.type}
          onChange={(event) => setValues({ ...values, type: event.target.value })}
        >
          {[...Object.entries(temp_controller_type_mapping)].map(([key, value]) => (
            <MenuItem value={key} selected>
              {value}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('add')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

export function DeleteDialog({ open, controller, wsManager, handleClose, command }) {
  let { t } = useTranslation();
  let { enqueSnackbar } = useSnackbar();
  const deleteController = (event) => {
    if (controller) {
      // If sending fails, repeat with force-delete.
      if (!wsManager.sendWSMsg(controller, command)) {
        wsManager.sendWSMsg(controller, 'delete_controller_force', {}, false, false, true);
      }
      handleClose();
    } else {
      enqueSnackbar('Conntollers or controller with id=' + controller.id + ' undefined.', {
        variant: 'error'
      });
      handleClose();
    }
  };

  if (controller) {
    return (
      <DialogWithEnterAction
        open={open}
        onClose={handleClose}
        onEnterAction={deleteController}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{t(command)}</DialogTitle>
        <DialogContent>
          {t(command + '_confirm')} <i>{controller.name}</i>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={deleteController} color="primary">
            {t('delete')}
          </Button>
        </DialogActions>
      </DialogWithEnterAction>
    );
  } else {
    return <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title"></Dialog>;
  }
}
